import { IfPermission } from '@app/core/auth/authorization/components/IfPermission';
import { useAvailableSocialExportPlatformsForStation } from '@app/modules/social-export/hooks/useAvailableSocialExportPlatformsForStation';
import { publishMessages } from '../publish-messages';
import { fragmentsPermissions } from '@mi-front/api-clients';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ShareIcon from '@mui/icons-material/Share';
import { Box, Button, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export interface VideoEditionPublishMenuProps {
  /** Station that drives which social platforms are offered. */
  stationId: string | undefined;
  /** Called with the selected platform id (e.g. YOUTUBE). */
  onSelectPlatform: (platformId: string) => void;
  /**
   * When true, only a share icon opens the menu (used next to the “published on …” label on edit cards).
   */
  iconOnly?: boolean;
}

/**
 * Shared "Publicar en" dropdown: lists station-connected platforms and delegates navigation to the caller.
 */
export function VideoEditionPublishMenu({
  stationId,
  onSelectPlatform,
  iconOnly = false,
}: VideoEditionPublishMenuProps) {
  const { $t } = useIntl();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { platforms: availablePlatforms } = useAvailableSocialExportPlatformsForStation(stationId);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePlatformSelect = (platformId: string) => {
    const platform = availablePlatforms.find(
      (p) => p.id.toUpperCase() === platformId.toUpperCase(),
    );
    if (platform) {
      onSelectPlatform(platform.id);
    }
    handleClose();
  };

  return (
    <IfPermission permission={fragmentsPermissions.Fragments_SocialPublish}>
      {availablePlatforms.length === 0 ? (
        <Typography variant="caption" color="text.disabled">
          <FormattedMessage {...publishMessages.noPlatformsToPublish} />
        </Typography>
      ) : (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {iconOnly ? (
            <IconButton
              size="small"
              color="primary"
              onClick={handleClick}
              title={$t(publishMessages.publishTo)}
              sx={{ px: 0.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ShareIcon fontSize="small" />
                <ArrowDropDownIcon fontSize="small" sx={{ ml: -0.25, mr: -0.25 }} />
              </Box>
            </IconButton>
          ) : (
            <Button
              size="small"
              onClick={handleClick}
              startIcon={<ShareIcon />}
              endIcon={<ArrowDropDownIcon />}
            >
              {$t(publishMessages.publishTo)}
            </Button>
          )}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {availablePlatforms.map((platform) => (
              <MenuItem key={platform.id} onClick={() => handlePlatformSelect(platform.id)}>
                {platform.Icon({ sx: { mr: 1, fontSize: 20 }, colored: true })} {platform.name}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}
    </IfPermission>
  );
}
